/**
 * Image URLs for the listing-detail gallery — ONE declaration, imported by both
 * responsive siblings.
 *
 * Why a module and not three inline calls: `listing-gallery-desktop.tsx` and
 * `mobile-listing-gallery.tsx` are copy-siblings that had already drifted (the
 * desktop strip passed a bare `unoptimized` and downloaded full originals; the
 * mobile strip went through `/_next/image` at `sizes="64px"`). Both MOUNT on
 * every pageview — `product-detail-view.tsx` toggles them with Tailwind
 * `hidden`, which is `display:none` and does NOT unmount (see
 * `tests/unit/fitness-listing-detail-triplet-single-import.test.ts`) — so any
 * divergence between them is paid twice in bytes.
 *
 * Why straight to Supabase instead of `/_next/image`: the optimizer adds a
 * server hop (fetch original -> decode -> re-encode). Measured on staging
 * 2026-07-28 against a 1080x1920 source stored at 68 080 B:
 * `/_next/image?w=1080&q=75` returned 84 819 B — LARGER than the source — while
 * Supabase's own resizer at the same width returned 51 168 B, and at 828 px
 * returned 33 694 B.
 *
 * HEIC path is preserved: `useHeicImage` yields a `blob:` URL (which the Next
 * optimizer cannot read anyway), and `toSupabasePublicImageThumbUrl` passes
 * `blob:` / `data:` / `/placeholder.svg` / non-storage refs through untouched.
 * That is why every call site renders these with `unoptimized` unconditionally
 * — the URL is already the size we want, or it is a local blob.
 *
 * @see lib/image-thumbnails.ts — the canonical builder (never fork it)
 * @see lib/images/presets.ts — the size policy (`CARD`)
 * @see tests/unit/listing-gallery-image-urls.test.ts — shape + cache-match pins
 */

import { CARD, SQUARE_SM, imagePresetUrl } from "@/lib/images/presets"
import type { ImageRefInput } from "@/lib/storage/public-url"

/**
 * The strip renders 64 CSS px squares; `SQUARE_SM` is 128, which covers a 2x DPR
 * screen. Its equal `height` makes the CDN return a real 128x128 crop, so the
 * CSS `object-cover` has nothing left to do and we ship the fewest possible
 * bytes. Nine thumbs on the reference listing: 731 564 B as full originals ->
 * 22 844 B here (measured staging 2026-07-28).
 *
 * Re-exported (rather than inlined) because the pin in
 * `tests/unit/listing-gallery-image-urls.test.ts` asserts against them and
 * because the strip is the surface these numbers were measured on.
 */
export const GALLERY_THUMB_SIZE = SQUARE_SM.width
export const GALLERY_THUMB_QUALITY = SQUARE_SM.quality

/**
 * Deliberately the SAME preset the card and the backdrop use, so all three URLs
 * on a feed -> detail navigation collapse into one browser-cache entry (and one
 * Supabase CDN entry) instead of three.
 *
 * The width itself and the reasoning for it live on `CARD` in
 * `lib/images/presets.ts` — one declaration, so the match cannot rot.
 * Box width there is an estimate, not a browser measurement; if it turns out to
 * be materially wider, `CARD.width` is the single place to revert.
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §12.1
 */
export const GALLERY_SLIDE_WIDTH = CARD.width
export const GALLERY_SLIDE_QUALITY = CARD.quality

/** 64 CSS px thumbnail-strip square. */
export function galleryThumbUrl(raw: ImageRefInput): string {
  return imagePresetUrl(raw, SQUARE_SM)
}

/**
 * The large slide. `CARD` carries no `height`, so the URL is an
 * aspect-preserving `resize=contain` downscale — exactly what the
 * `object-contain` slide wants (`cover` with a single dimension crops the
 * sides to a sliver; see the builder's comment).
 */
export function gallerySlideUrl(raw: ImageRefInput): string {
  return imagePresetUrl(raw, CARD)
}

/**
 * The blurred backdrop behind the slide.
 *
 * Deliberately re-emits the EXACT URL the main feed card already requested
 * (`components/feed/marketplace-vertical-listing-card.tsx` renders
 * `<ListingImage objectFit="contain">`, i.e. the card defaults + `contain`), so
 * a feed -> detail navigation paints it out of the browser HTTP cache instead
 * of re-downloading ~33 KB. The cache keys on the exact URL string and survives
 * a full page navigation, so string identity IS the whole optimisation — hence
 * the shared `CARD` preset rather than a repeated width literal.
 *
 * Crop mismatch with the card is irrelevant: this is `blur-2xl scale-110`.
 */
export function galleryBackdropUrl(raw: ImageRefInput): string {
  return imagePresetUrl(raw, CARD)
}
